<template>
    <DataTable  :value="logs"
                stripedRows
                dataKey="id"
                scrollable
                scrollHeight="60vh"
                lazy
                paginator
                :rows="limit"
                 :rowsPerPageOptions="[5, 10, 20, 50]"
                :totalRecords="logCount"
                @page="pageChanged($event)"
                :loading="loading"
                paginatorTemplate="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown"
                currentPageReportTemplate="{first} to {last} of {totalRecords}"
    >
        <Column field="content_type" header="Content Type"></Column>
        <Column field="action_display" header="Action"></Column>
        <Column field="object_repr" header="Object">
          <template #body="{ data }">
            {{formatGraphObject(data) }}
          </template>
        </Column>
        <Column field="changes" header="Changes">
            <template #body="{data}">
                <div v-if="data.changes && Object.keys(data.changes).length > 0 && data.action_display !== 'Delete'">
                    <div v-for="(val, key) in data.changes" :key="key">
                        {{ key }}: {{ formatChange(val) }}
                    </div>
                </div>
                <div v-else-if="data.action_display === 'Delete'">
                    {{ formatDeletion(data) }}
                </div>
                <div v-else-if="data.additional_data && data.additional_data.description">
                    {{ data.additional_data.description }}
                </div>
                <div v-else-if="data.additional_data && data.additional_data.event">
                    {{ data.additional_data.event }}
                </div>
            </template>
        </Column>
        <Column field="user" header="User">
            <template #body="{data}">
                {{ data.additional_data?.actor_label || data.additional_data?.actor_email || '' }}
            </template>
        </Column>
        <Column field="timestamp" header="Timestamp">
            <template #body="{data}">
                {{ parseDateTime(data.timestamp) }}
            </template>
        </Column>
        <Column name="ip_address" header="IP Address">
            <template #body="{data}">
                {{ data.additional_data?.ip_address || '' }}
            </template>
        </Column>

    </DataTable>
</template>
<script setup>
import {useStore} from "vuex";
import { onMounted, ref } from "vue";
import {parseDateTime} from "@/src/utils/utils";
import {OP, trackPending, usePending} from "@/src/queries/pending";
import {countAuditLogs, listAuditLogs} from "@/src/api/users";

const store = useStore()

const logCount = ref(0)
const logs = ref([])
const from = ref(0)
const limit = ref(10)

onMounted(async () => {
    await getCount()
    await getLogs()
})

const getCount = async () => {
    logCount.value = (await trackPending(OP.AUDIT_LOGS_COUNT, () => countAuditLogs())).count
}
const getLogs = async () => {
    logs.value = await trackPending(OP.GET_AUDIT_LOGS, () =>
        listAuditLogs({from: from.value, count: limit.value}))
}

const loading = usePending([OP.GET_AUDIT_LOGS, OP.AUDIT_LOGS_COUNT])

const formatChange = (val) => {
    /**
     * Formats the difference between old and new values
     * Returns: "old -> new" or just "new" if it was a creation
     */
    if (Array.isArray(val)) {
        if (val[0] === null || val[0] === undefined || val[0] === '') {
            return `${val[1]}`
        }
        return `${val[0]} -> ${val[1]}` 
    }
    return val
}

const formatDeletion = (data) => {
    const ct = (data.content_type || '').toLowerCase()
    let name = 'Unknown'
    if (data.changes?.name) {
        name = data.changes.name[0]
    } else {
        const m = (data.object_repr || '').match(/^\s*\d+\s*-\s*(.*)/)
        name = m ? m[1].trim() : (data.object_repr || 'Unknown')
    }
    
    if (ct === 'graph' || ct === 'graphs') {
        const workspace = data.changes?.workspace ? data.changes.workspace[0] : 'Unknown'
        return `Graph ${name} was deleted from the workspace ${workspace}`
    } else if (ct === 'workspace') {
        return `Workspace ${name} was deleted`
    }
    
    return `${data.content_type} ${name} was deleted`
}

const pageChanged = async (event) => {
    from.value = event.first
    limit.value = event.rows
    await getLogs()
}

const formatGraphObject = (row) => {
  const ct = (row.content_type || '').toLowerCase()
  const objId = row.object_id ?? row.object_pk

  if (ct === 'graph' || ct === 'graphs' || ct === 'workspace') {
    const s = row.object_repr || ''
    const label = ct === 'workspace' ? 'Workspace' : 'Graph'

    const m = s.match(/^\s*(\d+)\s*-\s*(.*)/)
    if (m) {
      const id = m[1]
      const name = m[2].trim()
      return `${label}: ${name} (${id})`
    }

    if (objId !== null && objId !== undefined) {
      return `${label}(${objId})`
    }
    return label
  }

  return row.object_repr || ''
}

</script>
<style scoped>
:deep(.p-datatable *) {
    font-family: 'IBM Plex Sans', sans-serif !important;
    font-size: 14px !important;
    font-weight: normal !important;
}
</style>